import React, { useEffect, useRef, useState } from "react";
import Dialog from "./confirmdialog";

/**
 * UnsavedChangesGuard
 * - Shows a custom confirmation when navigating via back button, hash changes, or link clicks.
 * - Shows native beforeunload prompt on refresh/close.
 *
 * Usage:
 *   <UnsavedChangesGuard isDirty={isFormDirty} />
 */
export default function UnsavedChangesGuard({ isDirty, formData, setFormData }: { isDirty: boolean, formData:{[s:string]:any}, setFormData:(s:any)=>void }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingNav, setPendingNav] = useState<null | { type: "back" | "hash" | "link"; href?: string; hash?: string }>(null);
  const disablingRef = useRef(false);           // temporarily disable guard while we complete a confirmed nav
  const prevHashRef = useRef<string>(window.location.hash);
  const revertingHashRef = useRef(false);       // avoid infinite loop when we revert a hash change
//   const initialPushedRef = useRef(false);       // track if we pushed a sentinel to history

  // 1) Refresh/close protection (native prompt)
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty || disablingRef.current) return;
      e.preventDefault();
      // Setting returnValue triggers the native confirmation dialog in supported browsers
      e.returnValue = "";
    };
    // const handleHashChange = () => {
    //     const tempForm={...formData}
    //     if (isDirty) {
    //       const confirmLeave = window.confirm('If you leave this page, your changes will be lost. Do you wish to continue?');
    //       if (!confirmLeave) {
    //         window.location.hash = prevHashRef.current;
    //         setFormData(tempForm)
    //         return false;
    //       } else{
    //         window.removeEventListener('hashchange', handleHashChange);
    //         return true
    //       }
    //     }
    //   };
    // // window.removeEventListener('hashchange', handleHashChange);
    window.removeEventListener("beforeunload", onBeforeUnload)
    // window.addEventListener('hashchange', handleHashChange);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
        // window.removeEventListener('hashchange', handleHashChange);
        window.removeEventListener("beforeunload", onBeforeUnload)
    };
  }, [isDirty]);

  // 2) Back button interception using history sentinel + popstate
//   useEffect(() => {
//     if (!initialPushedRef.current) {
//       // Push a sentinel state so there is always something to "pop" back to
//       try {
//         window.history.pushState({ __unsavedGuard: true }, "", window.location.href);
//         initialPushedRef.current = true;
//       } catch {}
//     }

//     const onPopState = () => {
//       if (!isDirty || disablingRef.current) return; // allow normal behavior when clean or while confirming
//       try {
//         // Immediately push back to cancel the back navigation
//         window.history.pushState({ __unsavedGuard: true }, "", window.location.href);
//       } catch {}
//       setPendingNav({ type: "back" });
//       setDialogOpen(true);
//     };

//     window.addEventListener("popstate", onPopState);
//     return () => window.removeEventListener("popstate", onPopState);
//   }, [isDirty]);

  // 3) Hash change interception
//   useEffect(() => {
//     const onHashChange = () => {
//       if (revertingHashRef.current) {
//         // We triggered this ourselves to revert; clear flag and accept
//         revertingHashRef.current = false;
//         prevHashRef.current = window.location.hash;
//         return;
//       }
//       if (!isDirty || disablingRef.current) {
//         prevHashRef.current = window.location.hash;
//         return;
//       }
//       // Block: revert hash immediately and ask
//       const desired = window.location.hash;
//       revertingHashRef.current = true;
//       window.location.hash = prevHashRef.current; // revert
//       setPendingNav({ type: "hash", hash: desired });
//       setDialogOpen(true);
//     };

//     window.addEventListener("hashchange", onHashChange);
//     return () => window.removeEventListener("hashchange", onHashChange);
//   }, [isDirty]);

//   4) Intercept in-app link clicks (left-click, same tab)
const onDocumentClick = (e: MouseEvent) => {
    if (!isDirty || disablingRef.current) return;

    // Ignore modified clicks/new-tabs
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    // Walk up DOM to find an <a href>
    let el = e.target as HTMLElement | null;
    while (el && el !== document.body) {
      if (el instanceof HTMLAnchorElement && el.href) {
        const href = el.href;
        // Only intercept if it's a real navigation in the same tab
        if (el.target && el.target !== "_self") return;
        // Prevent navigation and ask
        e.preventDefault();
        setPendingNav({ type: "link", href });
        setDialogOpen(true);
        return;
      }
      el = el.parentElement;
    }
  };
//   useEffect(() => {
//     document.addEventListener("click", onDocumentClick);
//     return () => document.removeEventListener("click", onDocumentClick);
//   }, [isDirty]);

  // Confirm/Cancel handlers
  const proceedNavigation = () => {
    if (!pendingNav) return;
    disablingRef.current = true;  // allow next navigation attempt through

    // Small delay lets the dialog close cleanly before we change location/history
    setDialogOpen(false);
    setTimeout(() => {
      if (pendingNav.type === "link" && pendingNav.href) {
        window.location.href = pendingNav.href;
      } else if (pendingNav.type === "back") {
        // Actually go back now that guard is disabled
        window.history.back();
      } else if (pendingNav.type === "hash" && pendingNav.hash) {
        // Apply the new hash
        prevHashRef.current = pendingNav.hash;
        window.location.hash = pendingNav.hash;
      }
      // Re-enable the guard shortly after navigation attempt (if we stayed on the page)
      setTimeout(() => {
        disablingRef.current = false;
      }, 500);
      setPendingNav(null);
    //   document.removeEventListener("click", onDocumentClick)
    }, 0);
  };

  const cancelNavigation = () => {
    // Stay on page, keep guard enabled
    setDialogOpen(false);
    setPendingNav(null);
  };

  return (
    <>
      {dialogOpen && (
        // <ConfirmDialog
        //   title="You have unsaved changes"
        //   message="If you leave this page, your changes will be lost."
        //   confirmLabel="Discard changes"
        //   cancelLabel="Stay on page"
        //   onConfirm={proceedNavigation}
        //   onCancel={cancelNavigation}
        // />
        <Dialog
            isOpen={dialogOpen}
            title="You have unsaved changes"
            height={120}
            className="ui-front ui-dialog-buttons left-align-text"
            buttons={[
                {
                    label: 'Stay on Page',
                    className: 'ui-button ui-corner-all ui-widget app-button negative',
                    onClick: cancelNavigation,
                },
                {
                    label: 'Discard Changes',
                    className: 'ui-button ui-corner-all ui-widget app-button',
                    onClick: proceedNavigation,
                },
            ]}
            hideClose={true}
        >
            If you leave this page, your changes will be lost. Do you wish to continue?
        </Dialog>
      )}
    </>
  );
}

// /** Minimal, accessible confirm dialog (no styling framework required) */
// function ConfirmDialog({
//   title,
//   message,
//   confirmLabel,
//   cancelLabel,
//   onConfirm,
//   onCancel,
// }: {
//   title: string;
//   message: string;
//   confirmLabel: string;
//   cancelLabel: string;
//   onConfirm: () => void;
//   onCancel: () => void;
// }) {
//   // Basic focus trap is omitted for brevity; add your dialog lib if you have one
//   return (
//     <div
//       role="dialog"
//       aria-modal="true"
//       aria-labelledby="unsaved-title"
//       className="unsaved-overlay"
//       style={{
//         position: "fixed",
//         inset: 0,
//         background: "rgba(0,0,0,0.4)",
//         display: "grid",
//         placeItems: "center",
//         zIndex: 9999,
//       }}
//     >
//       <div
//         style={{
//           background: "white",
//           borderRadius: 12,
//           padding: 20,
//           width: "min(420px, 90vw)",
//           boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
//         }}
//       >
//         <h2 id="unsaved-title" style={{ marginTop: 0, marginBottom: 8 }}>{title}</h2>
//         <p style={{ marginTop: 0 }}>{message}</p>
//         <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
//           <button onClick={onCancel} style={{ padding: "8px 12px" }}>
//             {cancelLabel}
//           </button>
//           <button onClick={onConfirm} style={{ padding: "8px 12px", fontWeight: 600 }}>
//             {confirmLabel}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }
